import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Calendar, Users, Image, Tag, LayoutDashboard, Plus, Edit, Trash2, 
  Eye, ChevronLeft, Search, Filter, MoreVertical, Building2, Check, X,
  Award, ExternalLink, Lock, LogOut
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ImageUpload } from "../components/ImageUpload";

const API = `/api`;
const UPLOADS_URL = `/api/uploads`;

const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/events", icon: Calendar, label: "Events" },
  { path: "/admin/gallery", icon: Image, label: "Gallery" },
  { path: "/admin/promotions", icon: Tag, label: "Promotions" },
  { path: "/admin/leads", icon: Users, label: "Leads" },
];

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const storedToken = localStorage.getItem("sag_admin_token");
    if (storedToken) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("sag_admin_token", res.data.token);
      setIsAuthenticated(true);
      toast.success("Login successful!");
    } catch (error) {
      toast.error("Invalid password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sag_admin_token");
    setIsAuthenticated(false);
    setPassword("");
    toast.success("Logged out successfully.");
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#152A47] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-[#1E3A5F] rounded-xl border border-white/10 p-8 shadow-2xl">
            <div className="text-center mb-8">
              <img 
                src={`${UPLOADS_URL}/sag-logo.png`} 
                alt="SAG"
                className="h-16 w-auto mx-auto mb-4"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="w-14 h-14 bg-[#00AEEF]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-[#00AEEF]" />
              </div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-[#94A3B8] mt-2 text-sm">Enter your password to access the admin panel</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-[#94A3B8] text-sm">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  className="bg-[#152A47] border-[#2B4A6F] text-white placeholder:text-gray-400 mt-1 h-12"
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full bg-[#00AEEF] hover:bg-[#0095CC] h-12 text-base font-semibold"
                data-testid="admin-login-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/" className="text-[#64748B] hover:text-white text-sm transition-colors">
                ← Back to Public Site
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#152A47] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1E3A5F] border-r border-white/10 fixed left-0 top-0 bottom-0 z-40">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <img 
              src={`${UPLOADS_URL}/sag-logo.png`} 
              alt="SAG"
              className="h-10 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <h1 className="text-white font-semibold text-sm">SAG Admin</h1>
              <p className="text-[#64748B] text-xs">Events Portal</p>
            </div>
          </Link>
        </div>
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== "/admin" && location.pathname.startsWith(item.path));
            return (
              <Link 
                key={item.path} 
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                  isActive 
                    ? "bg-[#00AEEF]/20 text-[#00AEEF] border-l-2 border-[#00AEEF]" 
                    : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
          
          {/* W.I.N Summit 2026 Section */}
          <div className="pt-4 mt-4 border-t border-white/10">
            <p className="px-4 text-xs text-[#64748B] uppercase tracking-wider mb-2">Partner Events</p>
            <Link 
              to="/admin/win-2026"
              className="flex items-center gap-3 px-4 py-3 rounded transition-colors bg-gradient-to-r from-[#008B9B]/20 to-[#D4AF37]/20 text-[#D4AF37] hover:from-[#008B9B]/30 hover:to-[#D4AF37]/30 border border-[#D4AF37]/30"
              data-testid="admin-nav-win2026"
            >
              <Award className="w-5 h-5" />
              <div className="flex-1">
                <span className="text-sm font-medium block">W.I.N Summit 2026</span>
                <span className="text-xs text-[#008B9B]">Registrations & Admin</span>
              </div>
              <ExternalLink className="w-4 h-4 opacity-50" />
            </Link>
          </div>
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 space-y-2">
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm transition-colors w-full"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
          <Link to="/" className="flex items-center gap-2 text-[#64748B] hover:text-white text-sm transition-colors">
            <Eye className="w-4 h-4" />
            View Public Site
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="events/*" element={<EventsManager />} />
          <Route path="gallery/*" element={<GalleryManager />} />
          <Route path="promotions/*" element={<PromotionsManager />} />
          <Route path="leads" element={<LeadsManager />} />
        </Routes>
      </main>
    </div>
  );
}

function DashboardOverview() {
  const [stats, setStats] = useState({ events: 0, gallery: 0, promotions: 0, leads: 0 });
  const [recentLeads, setRecentLeads] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [events, gallery, promotions, leads] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/promotions`),
        axios.get(`${API}/leads`)
      ]);
      setStats({
        events: events.data.length,
        gallery: gallery.data.length,
        promotions: promotions.data.length,
        leads: leads.data.length
      });
      setRecentLeads(leads.data.slice(-5).reverse());
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const seedDatabase = async () => {
    try {
      await axios.post(`${API}/seed`);
      toast.success("Sample data created successfully!");
      fetchStats();
    } catch (error) {
      toast.error("Failed to seed database");
    }
  };

  const statCards = [
    { label: "Total Events", value: stats.events, icon: Calendar, color: "blue" },
    { label: "Gallery Albums", value: stats.gallery, icon: Image, color: "purple" },
    { label: "Active Promotions", value: stats.promotions, icon: Tag, color: "amber" },
    { label: "Total Leads", value: stats.leads, icon: Users, color: "green" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-[#64748B]">Overview of your events platform</p>
        </div>
        <Button onClick={seedDatabase} className="bg-[#FFCC00] hover:bg-[#FFE066] text-[#152A47]" data-testid="seed-data-btn">
          Load Sample Data
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <motion.div 
            key={stat.label}
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <stat.icon className={`w-8 h-8 text-${stat.color}-500`} />
              <span className={`text-3xl font-bold text-white`}>{stat.value}</span>
            </div>
            <p className="text-[#94A3B8] text-sm">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* W.I.N Summit 2026 Featured Card */}
      <motion.div 
        className="mb-8 p-6 rounded-xl bg-gradient-to-r from-[#008B9B] to-[#006064] relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center">
              <Award className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">W.I.N Entrepreneurial Summit 2026</h3>
              <p className="text-white/70 text-sm">March 27-29, 2026 | Partner Event with Tourism Development Corporation</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => window.open("/win2026", "_blank")}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Public Page
            </Button>
            <Button 
              onClick={() => navigate("/admin/win-2026")}
              className="bg-[#D4AF37] hover:bg-[#B4941F] text-[#1E3A5F]"
              data-testid="manage-win2026-btn"
            >
              Manage Registrations
            </Button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Recent Leads</h2>
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/leads")} className="text-[#00AEEF]">
              View All
            </Button>
          </div>
          {recentLeads.length === 0 ? (
            <p className="text-[#64748B] text-center py-8">No leads yet</p>
          ) : (
            <div className="space-y-3">
              {recentLeads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-3 bg-[#152A47]/50 rounded">
                  <div>
                    <p className="text-white font-medium">{lead.name}</p>
                    <p className="text-[#64748B] text-sm">{lead.email}</p>
                  </div>
                  <span className="px-2 py-1 bg-[#00AEEF]/20 text-[#00AEEF] text-xs rounded">
                    {lead.lead_type.replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <Button 
              onClick={() => navigate("/admin/events")} 
              variant="outline" 
              className="h-24 flex-col gap-2 border-[#2B4A6F] hover:border-[#00AEEF] hover:text-[#00AEEF]"
              data-testid="quick-action-events"
            >
              <Calendar className="w-6 h-6" />
              <span>Add Event</span>
            </Button>
            <Button 
              onClick={() => navigate("/admin/gallery")} 
              variant="outline" 
              className="h-24 flex-col gap-2 border-[#2B4A6F] hover:border-[#00AEEF] hover:text-[#00AEEF]"
              data-testid="quick-action-gallery"
            >
              <Image className="w-6 h-6" />
              <span>Add Album</span>
            </Button>
            <Button 
              onClick={() => navigate("/admin/promotions")} 
              variant="outline" 
              className="h-24 flex-col gap-2 border-[#2B4A6F] hover:border-[#FFCC00] hover:text-[#FFCC00]"
              data-testid="quick-action-promotions"
            >
              <Tag className="w-6 h-6" />
              <span>Add Promotion</span>
            </Button>
            <Button 
              onClick={() => navigate("/admin/leads")} 
              variant="outline" 
              className="h-24 flex-col gap-2 border-[#2B4A6F] hover:border-green-500 hover:text-green-400"
              data-testid="quick-action-leads"
            >
              <Users className="w-6 h-6" />
              <span>View Leads</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventsManager() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filter, setFilter] = useState("all");
  const [formData, setFormData] = useState({
    title: "", description: "", event_type: "upcoming", status: "published",
    date_start: "", date_end: "", location: "", partner_name: "", 
    image_url: "", cta_text: "Register Interest", tags: "", is_featured: false,
    partner_email: ""
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (error) {
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        ...event,
        tags: event.tags?.join(", ") || ""
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: "", description: "", event_type: "upcoming", status: "published",
        date_start: "", date_end: "", location: "", partner_name: "", 
        image_url: "", cta_text: "Register Interest", tags: "", is_featured: false,
        embed_code: "", partner_email: ""
      });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()).filter(Boolean) : []
      };
      
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, data);
        toast.success("Event updated successfully!");
      } else {
        await axios.post(`${API}/events`, data);
        toast.success("Event created successfully!");
      }
      setModalOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to save event");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success("Event deleted successfully!");
      setDeleteConfirm(null);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const filteredEvents = filter === "all" ? events : events.filter(e => e.event_type === filter);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Events</h1>
          <p className="text-[#64748B]">Manage all your events</p>
        </div>
        <Button onClick={() => openModal()} className="bg-[#00AEEF] hover:bg-[#0095CC]" data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Event
        </Button>
      </div>

      <div className="glass-card p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <Input 
              placeholder="Search events..." 
              className="bg-[#152A47] border-[#2B4A6F] text-white pl-10"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-[#152A47] border-[#2B4A6F] text-white" data-testid="event-filter">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E3A5F] border-[#2B4A6F]">
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="popup">Pop-up</SelectItem>
              <SelectItem value="past">Past</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Type</th>
              <th>Date</th>
              <th>Partner</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
            ) : filteredEvents.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No events found</td></tr>
            ) : (
              filteredEvents.map((event) => (
                <tr key={event.id} data-testid={`event-row-${event.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      {event.image_url && (
                        <img src={event.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                      )}
                      <div>
                        <p className="text-white font-medium">{event.title}</p>
                        <p className="text-[#64748B] text-xs truncate max-w-xs">{event.description}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      event.event_type === "upcoming" ? "bg-[#00AEEF]/20 text-[#00AEEF]" :
                      event.event_type === "popup" ? "bg-amber-500/20 text-amber-400" :
                      "bg-slate-500/20 text-slate-400"
                    }`}>
                      {event.event_type}
                    </span>
                  </td>
                  <td className="text-[#94A3B8]">{formatDate(event.date_start)}</td>
                  <td className="text-[#94A3B8]">{event.partner_name || "-"}</td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      event.status === "published" ? "status-published" :
                      event.status === "draft" ? "status-draft" : "status-archived"
                    }`}>
                      {event.status}
                    </span>
                  </td>
                  <td className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`event-actions-${event.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#1E3A5F] border-[#2B4A6F]">
                        <DropdownMenuItem onClick={() => openModal(event)} className="cursor-pointer">
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setDeleteConfirm(event)} 
                          className="cursor-pointer text-red-400"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Event Form Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#1E3A5F] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-modal">
          <DialogHeader>
            <DialogTitle className="text-white">{editingEvent ? "Edit Event" : "Add New Event"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-[#94A3B8]">Title *</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="event-title-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[#94A3B8]">Description *</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1 min-h-[100px]"
                  data-testid="event-description-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">Event Type *</Label>
                <Select value={formData.event_type} onValueChange={(v) => setFormData({...formData, event_type: v})}>
                  <SelectTrigger className="bg-[#152A47] border-[#2B4A6F] text-white mt-1" data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E3A5F] border-[#2B4A6F]">
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="popup">Pop-up (B2B)</SelectItem>
                    <SelectItem value="past">Past</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger className="bg-[#152A47] border-[#2B4A6F] text-white mt-1" data-testid="event-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E3A5F] border-[#2B4A6F]">
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8]">Start Date *</Label>
                <Input 
                  type="date"
                  value={formData.date_start} 
                  onChange={(e) => setFormData({...formData, date_start: e.target.value})}
                  required
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="event-date-start-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">End Date</Label>
                <Input 
                  type="date"
                  value={formData.date_end || ""} 
                  onChange={(e) => setFormData({...formData, date_end: e.target.value})}
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="event-date-end-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">Location</Label>
                <Input 
                  value={formData.location || ""} 
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g., Nassau, Bahamas"
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="event-location-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">Partner Name</Label>
                <Input 
                  value={formData.partner_name || ""} 
                  onChange={(e) => setFormData({...formData, partner_name: e.target.value})}
                  placeholder="e.g., Scotiabank"
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="event-partner-input"
                />
              </div>
              <div className="col-span-2">
                <ImageUpload
                  label="Event Image"
                  value={formData.image_url || ""}
                  onChange={(url) => setFormData({...formData, image_url: url})}
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">CTA Button Text</Label>
                <Input 
                  value={formData.cta_text || ""} 
                  onChange={(e) => setFormData({...formData, cta_text: e.target.value})}
                  placeholder="Register Interest"
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="event-cta-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">Tags (comma separated)</Label>
                <Input 
                  value={formData.tags || ""} 
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  placeholder="summit, finance, networking"
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="event-tags-input"
                />
              </div>
              <div className="col-span-2 flex items-center gap-3">
                <Switch 
                  checked={formData.is_featured} 
                  onCheckedChange={(v) => setFormData({...formData, is_featured: v})}
                  data-testid="event-featured-switch"
                />
                <Label className="text-[#94A3B8]">Featured Event</Label>
              </div>
              <div className="col-span-2">
                <Label className="text-[#94A3B8]">Partner Notification Email</Label>
                <p className="text-xs text-[#64748B] mb-2">Partner will receive email notifications when leads are submitted for this event.</p>
                <Input 
                  type="email"
                  value={formData.partner_email || ""} 
                  onChange={(e) => setFormData({...formData, partner_email: e.target.value})}
                  placeholder="partner@company.com"
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="event-partner-email-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[#94A3B8]">Embed Code (HTML)</Label>
                <p className="text-xs text-[#64748B] mb-2">Paste iframe or embed code from Facebook, YouTube, etc.</p>
                <Textarea 
                  value={formData.embed_code || ""} 
                  onChange={(e) => setFormData({...formData, embed_code: e.target.value})}
                  placeholder='<iframe src="https://www.facebook.com/plugins/post.php?..." width="500" height="600"></iframe>'
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1 min-h-[120px] font-mono text-sm"
                  data-testid="event-embed-input"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="border-[#2B4A6F]">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#00AEEF] hover:bg-[#0095CC]" data-testid="event-submit-btn">
                {editingEvent ? "Update Event" : "Create Event"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#1E3A5F] border-white/10" data-testid="delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Event</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Are you sure you want to delete "{deleteConfirm?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-[#2B4A6F]">
              Cancel
            </Button>
            <Button 
              onClick={() => handleDelete(deleteConfirm.id)} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GalleryManager() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    title: "", description: "", partner_name: "", cover_image: "", images: "", tags: ""
  });

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setAlbums(res.data);
    } catch (error) {
      toast.error("Failed to fetch gallery");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (album = null) => {
    if (album) {
      setEditingAlbum(album);
      setFormData({
        title: album.title,
        description: album.description || "",
        partner_name: album.partner_name || "",
        cover_image: album.cover_image || "",
        images: album.images?.join("\n") || "",
        tags: album.tags?.join(", ") || ""
      });
    } else {
      setEditingAlbum(null);
      setFormData({ title: "", description: "", partner_name: "", cover_image: "", images: "", tags: "" });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        images: formData.images ? formData.images.split("\n").map(i => i.trim()).filter(Boolean) : [],
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()).filter(Boolean) : []
      };
      
      if (editingAlbum) {
        await axios.put(`${API}/gallery/${editingAlbum.id}`, data);
        toast.success("Album updated!");
      } else {
        await axios.post(`${API}/gallery`, data);
        toast.success("Album created!");
      }
      setModalOpen(false);
      fetchAlbums();
    } catch (error) {
      toast.error("Failed to save album");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`);
      toast.success("Album deleted!");
      setDeleteConfirm(null);
      fetchAlbums();
    } catch (error) {
      toast.error("Failed to delete album");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Gallery</h1>
          <p className="text-[#64748B]">Manage photo albums from past events</p>
        </div>
        <Button onClick={() => openModal()} className="bg-[#00AEEF] hover:bg-[#0095CC]" data-testid="add-album-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Album
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B]">Loading...</div>
      ) : albums.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Image className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
          <p className="text-[#94A3B8]">No albums yet. Create your first album!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {albums.map((album) => (
            <div key={album.id} className="glass-card overflow-hidden group" data-testid={`album-card-${album.id}`}>
              <div className="relative h-48">
                <img 
                  src={album.cover_image || album.images?.[0] || "https://images.unsplash.com/photo-1762158007817-e149aab49270?w=800"} 
                  alt={album.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <Button size="icon" variant="secondary" onClick={() => openModal(album)} className="h-8 w-8">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="destructive" onClick={() => setDeleteConfirm(album)} className="h-8 w-8">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-white font-medium">{album.title}</h3>
                {album.partner_name && (
                  <p className="text-[#CCA43B] text-sm flex items-center gap-1 mt-1">
                    <Building2 className="w-3 h-3" /> {album.partner_name}
                  </p>
                )}
                <p className="text-[#64748B] text-xs mt-2">{album.images?.length || 0} photos</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Album Form Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#1E3A5F] border-white/10 max-w-lg" data-testid="album-modal">
          <DialogHeader>
            <DialogTitle className="text-white">{editingAlbum ? "Edit Album" : "Add New Album"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#94A3B8]">Title *</Label>
              <Input 
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                data-testid="album-title-input"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                data-testid="album-description-input"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Partner Name</Label>
              <Input 
                value={formData.partner_name} 
                onChange={(e) => setFormData({...formData, partner_name: e.target.value})}
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                data-testid="album-partner-input"
              />
            </div>
            <div>
              <ImageUpload
                label="Cover Image"
                value={formData.cover_image}
                onChange={(url) => setFormData({...formData, cover_image: url})}
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Image URLs (one per line)</Label>
              <Textarea 
                value={formData.images} 
                onChange={(e) => setFormData({...formData, images: e.target.value})}
                placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1 min-h-[100px]"
                data-testid="album-images-input"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Tags (comma separated)</Label>
              <Input 
                value={formData.tags} 
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                data-testid="album-tags-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="border-[#2B4A6F]">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#00AEEF] hover:bg-[#0095CC]" data-testid="album-submit-btn">
                {editingAlbum ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#1E3A5F] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Album</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Are you sure you want to delete "{deleteConfirm?.title}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-[#2B4A6F]">Cancel</Button>
            <Button onClick={() => handleDelete(deleteConfirm.id)} className="bg-red-600 hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PromotionsManager() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    title: "", description: "", partner_name: "", image_url: "",
    date_start: "", date_end: "", cta_text: "Request Info",
    disclaimer: "Promotions are presented for informational purposes. Final terms and fulfillment are provided by the third-party provider.",
    is_active: true
  });

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const res = await axios.get(`${API}/promotions`);
      setPromotions(res.data);
    } catch (error) {
      toast.error("Failed to fetch promotions");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setFormData(promo);
    } else {
      setEditingPromo(null);
      setFormData({
        title: "", description: "", partner_name: "", image_url: "",
        date_start: "", date_end: "", cta_text: "Request Info",
        disclaimer: "Promotions are presented for informational purposes. Final terms and fulfillment are provided by the third-party provider.",
        is_active: true
      });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPromo) {
        await axios.put(`${API}/promotions/${editingPromo.id}`, formData);
        toast.success("Promotion updated!");
      } else {
        await axios.post(`${API}/promotions`, formData);
        toast.success("Promotion created!");
      }
      setModalOpen(false);
      fetchPromotions();
    } catch (error) {
      toast.error("Failed to save promotion");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/promotions/${id}`);
      toast.success("Promotion deleted!");
      setDeleteConfirm(null);
      fetchPromotions();
    } catch (error) {
      toast.error("Failed to delete promotion");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Promotions</h1>
          <p className="text-[#64748B]">Manage third-party partner promotions</p>
        </div>
        <Button onClick={() => openModal()} className="bg-[#00AEEF] hover:bg-[#0095CC]" data-testid="add-promotion-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Promotion
        </Button>
      </div>

      <div className="glass-card overflow-hidden">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Promotion</th>
              <th>Partner</th>
              <th>Period</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
            ) : promotions.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">No promotions yet</td></tr>
            ) : (
              promotions.map((promo) => (
                <tr key={promo.id} data-testid={`promotion-row-${promo.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      {promo.image_url && (
                        <img src={promo.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                      )}
                      <div>
                        <p className="text-white font-medium">{promo.title}</p>
                        <p className="text-[#64748B] text-xs truncate max-w-xs">{promo.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="text-[#CCA43B]">{promo.partner_name}</td>
                  <td className="text-[#94A3B8] text-sm">
                    {formatDate(promo.date_start)} - {formatDate(promo.date_end)}
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      promo.is_active ? "bg-green-500/20 text-green-400" : "bg-slate-500/20 text-slate-400"
                    }`}>
                      {promo.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#1E3A5F] border-[#2B4A6F]">
                        <DropdownMenuItem onClick={() => openModal(promo)} className="cursor-pointer">
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteConfirm(promo)} className="cursor-pointer text-red-400">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Promotion Form Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#1E3A5F] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promotion-modal">
          <DialogHeader>
            <DialogTitle className="text-white">{editingPromo ? "Edit Promotion" : "Add New Promotion"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#94A3B8]">Title *</Label>
              <Input 
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                data-testid="promotion-title-input"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Description *</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1 min-h-[100px]"
                data-testid="promotion-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8]">Partner Name *</Label>
                <Input 
                  value={formData.partner_name} 
                  onChange={(e) => setFormData({...formData, partner_name: e.target.value})}
                  required
                  placeholder="e.g., Endless Dream Vacations"
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="promotion-partner-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">CTA Button Text</Label>
                <Input 
                  value={formData.cta_text} 
                  onChange={(e) => setFormData({...formData, cta_text: e.target.value})}
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="promotion-cta-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">Start Date *</Label>
                <Input 
                  type="date"
                  value={formData.date_start} 
                  onChange={(e) => setFormData({...formData, date_start: e.target.value})}
                  required
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="promotion-date-start-input"
                />
              </div>
              <div>
                <Label className="text-[#94A3B8]">End Date *</Label>
                <Input 
                  type="date"
                  value={formData.date_end} 
                  onChange={(e) => setFormData({...formData, date_end: e.target.value})}
                  required
                  className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                  data-testid="promotion-date-end-input"
                />
              </div>
            </div>
            <div>
              <ImageUpload
                label="Promotion Image"
                value={formData.image_url || ""}
                onChange={(url) => setFormData({...formData, image_url: url})}
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Disclaimer</Label>
              <Textarea 
                value={formData.disclaimer} 
                onChange={(e) => setFormData({...formData, disclaimer: e.target.value})}
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                data-testid="promotion-disclaimer-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch 
                checked={formData.is_active} 
                onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                data-testid="promotion-active-switch"
              />
              <Label className="text-[#94A3B8]">Active</Label>
            </div>
            <div>
              <Label className="text-[#94A3B8]">Partner Notification Email</Label>
              <p className="text-xs text-[#64748B] mb-1">Partner will receive email notifications for leads on this promotion.</p>
              <Input 
                type="email"
                value={formData.partner_email || ""} 
                onChange={(e) => setFormData({...formData, partner_email: e.target.value})}
                placeholder="partner@company.com"
                className="bg-[#152A47] border-[#2B4A6F] text-white mt-1"
                data-testid="promotion-partner-email-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="border-[#2B4A6F]">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#00AEEF] hover:bg-[#0095CC]" data-testid="promotion-submit-btn">
                {editingPromo ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#1E3A5F] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Promotion</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Are you sure you want to delete "{deleteConfirm?.title}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-[#2B4A6F]">Cancel</Button>
            <Button onClick={() => handleDelete(deleteConfirm.id)} className="bg-red-600 hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LeadsManager() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data);
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success("Lead deleted!");
      setDeleteConfirm(null);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const filteredLeads = filter === "all" ? leads : leads.filter(l => l.lead_type === filter);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", { 
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" 
    });
  };

  const leadTypeLabels = {
    register_interest: "Register Interest",
    request_callback: "Request Callback",
    become_partner: "Become Partner"
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Leads</h1>
          <p className="text-[#64748B]">View and manage all lead submissions</p>
        </div>
      </div>

      <div className="glass-card p-4 mb-6">
        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 bg-[#152A47] border-[#2B4A6F] text-white" data-testid="lead-filter">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E3A5F] border-[#2B4A6F]">
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="register_interest">Register Interest</SelectItem>
              <SelectItem value="request_callback">Request Callback</SelectItem>
              <SelectItem value="become_partner">Become Partner</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[#64748B] text-sm">{filteredLeads.length} leads</span>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Contact</th>
              <th>Type</th>
              <th>Company</th>
              <th>Source</th>
              <th>Date</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
            ) : filteredLeads.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No leads yet</td></tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                  <td>
                    <div>
                      <p className="text-white font-medium">{lead.name}</p>
                      <p className="text-[#64748B] text-xs">{lead.email}</p>
                      {lead.phone && <p className="text-[#64748B] text-xs">{lead.phone}</p>}
                    </div>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      lead.lead_type === "register_interest" ? "bg-[#00AEEF]/20 text-[#00AEEF]" :
                      lead.lead_type === "request_callback" ? "bg-green-500/20 text-green-400" :
                      "bg-amber-500/20 text-amber-400"
                    }`}>
                      {leadTypeLabels[lead.lead_type] || lead.lead_type}
                    </span>
                  </td>
                  <td className="text-[#94A3B8]">{lead.company || "-"}</td>
                  <td className="text-[#64748B] text-sm">{lead.source || "-"}</td>
                  <td className="text-[#64748B] text-sm">{formatDate(lead.created_at)}</td>
                  <td className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setDeleteConfirm(lead)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#1E3A5F] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Lead</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Are you sure you want to delete the lead from "{deleteConfirm?.name}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-[#2B4A6F]">Cancel</Button>
            <Button onClick={() => handleDelete(deleteConfirm.id)} className="bg-red-600 hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
